import { WsmErrorCode, WsmErrorFragment } from "@dashboard/graphql";
import commonErrorMessages, { getCommonFormFieldErrorMessage } from "@dashboard/utils/errors/common";
import { defineMessages, IntlShape } from "react-intl";

// The kit members of WsmErrorCode. The backend sends its own merchant
// sentence in `message` and the Dashboard renders that verbatim; these strings
// are the fallback for a payload that carries a code and no sentence.
export const wsmKitsErrorMessages = defineMessages({
  duplicateKitMember: {
    id: 'la0zP7',
    defaultMessage: "This variant is already a member of the kit",
  },
  kitMemberQuantityBelowOne: {
    id: 'OMt3vS',
    defaultMessage: "A member quantity has to be at least 1",
  },
  ruleSubjectNotInKit: {
    id: 'fbsBbe',
    defaultMessage: "The rule subject has to be a member of this kit",
  },
  ruleTargetNotInKit: {
    id: 'Sq7V9a',
    defaultMessage: "Every rule target has to be a member of this kit",
  },
});

const codeToMessage = {
  [WsmErrorCode.DUPLICATE_KIT_MEMBER]: wsmKitsErrorMessages.duplicateKitMember,
  [WsmErrorCode.KIT_MEMBER_QUANTITY_BELOW_ONE]:
    wsmKitsErrorMessages.kitMemberQuantityBelowOne,
  [WsmErrorCode.RULE_SUBJECT_NOT_IN_KIT]: wsmKitsErrorMessages.ruleSubjectNotInKit,
  [WsmErrorCode.RULE_TARGET_NOT_IN_KIT]: wsmKitsErrorMessages.ruleTargetNotInKit,
} as Partial<Record<WsmErrorCode, (typeof wsmKitsErrorMessages)["duplicateKitMember"]>>;

/**
 * The model's own sentence first, this module's wording second, the Dashboard's
 * common wording last. Returns undefined for no error, so a helperText prop can
 * take the result directly.
 */
export const getWsmKitsErrorMessage = (
  error: WsmErrorFragment | undefined,
  intl: IntlShape,
): string | undefined => {
  if (!error) {
    return undefined;
  }

  if (error.message) {
    return error.message;
  }

  const message = error.code ? codeToMessage[error.code] : undefined;

  if (message) {
    return intl.formatMessage(message);
  }

  return getCommonFormFieldErrorMessage({ ...error, code: error.code }, intl);
};

export const wsmKitsUnknownErrorMessage = commonErrorMessages.unknownError;

/**
 * The backend names a row inside a list input as `<list>.<index>.<field>`, the
 * same shape `wsmTierPriceBulkCreate` uses. This turns that flat list into one
 * error per row index so a members or rules datagrid can show it on the row the
 * merchant has to fix, which is the whole point of RULE_TARGET_NOT_IN_KIT.
 */
export const getWsmIndexedErrors = (
  errors: WsmErrorFragment[],
  list: "members" | "rules",
): Record<number, WsmErrorFragment> =>
  errors.reduce<Record<number, WsmErrorFragment>>((acc, error) => {
    const match = error.field?.match(new RegExp(`^${list}\\.(\\d+)(\\.|$)`));

    if (match) {
      acc[Number(match[1])] = error;
    }

    return acc;
  }, {});
